import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Code,
  HStack,
  Link,
  List,
  ListItem,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { orderService } from '@/services/admin/orderService'
import { useToast } from '@/hooks/useToast'
import type {
  OrderDocumentDetail,
  OrderDocumentSummary,
  OrderDocumentsResponse,
  OrderDocumentType,
} from '@/types'
import type { NetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { formatDateTime } from '@/utils/formatUtils'

interface OrderLifecycleDocumentsTabProps {
  orderId?: string | null
  isActive: boolean
  mode: NetworkEnvironment
  onRefresh?: () => void
  refreshToken: number
  onLoadingChange?: (isLoading: boolean) => void
}

const DOCUMENT_TYPES: OrderDocumentType[] = ['invoice', 'receipt']

const truncateMiddle = (value?: string | null, length = 12) => {
  if (!value) return ''
  if (value.length <= length * 2) return value
  return `${value.slice(0, length)}...${value.slice(-length)}`
}

const formatTimestamp = (value?: string | null, locale?: string) => {
  if (!value) {
    return null
  }
  return formatDateTime(value, {
    locale: locale ?? (typeof window !== 'undefined' ? navigator.language : 'en-US'),
  })
}

const formatFileSize = (value?: number | null): string | null => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return null
  }
  if (value < 1024) {
    return `${value} B`
  }
  if (value < 1024 * 1024) {
    return `${(value / 1024).toFixed(1)} KB`
  }
  return `${(value / (1024 * 1024)).toFixed(2)} MB`
}

const buildDocumentDownloadFilename = (
  type: OrderDocumentType,
  documentNumber?: string | null,
  mode?: NetworkEnvironment
): string => {
  const title = type === 'receipt' ? 'Receipt' : 'Invoice'
  if (!documentNumber) {
    return `${title}.pdf`
  }
  const envSuffix = mode === 'testnet' ? '-testnet' : ''
  const publicDocumentCode = documentNumber.replace(/^(Inv|Rct)-/u, '')
  return `${title}${envSuffix}-${publicDocumentCode}.pdf`
}

const getSnapshotString = (snapshot: Record<string, unknown> | null | undefined, key: string): string | null => {
  const value = snapshot?.[key]
  return typeof value === 'string' && value.trim().length > 0 ? value : null
}

const stringifyMetadataValue = (value: unknown): string => {
  if (typeof value === 'string') {
    return value
  }
  if (typeof value === 'number' || typeof value === 'boolean' || value === null) {
    return String(value)
  }
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

const getMetadataEntries = (
  snapshot: Record<string, unknown> | null | undefined
): Array<{ key: string; displayValue: string; charLength: number }> => {
  const candidate = snapshot?.metadata
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
    return []
  }

  return Object.entries(candidate as Record<string, unknown>)
    .map(([key, value]) => {
      const rawValue = stringifyMetadataValue(value)
      return {
        key,
        displayValue: rawValue,
        charLength: rawValue.length,
      }
    })
}

const getReceiptPayments = (
  snapshot: Record<string, unknown> | null | undefined
): Array<{ txHash: string; fromAddress: string; amountUsd: string }> => {
  const candidate = snapshot?.payments
  if (!Array.isArray(candidate)) {
    return []
  }

  return candidate
    .map(entry => {
      if (!entry || typeof entry !== 'object') {
        return null
      }
      const record = entry as Record<string, unknown>
      const txHash = typeof record.txHash === 'string' ? record.txHash : ''
      const fromAddress = typeof record.fromAddress === 'string' ? record.fromAddress : ''
      const amountUsd = typeof record.amountUsd === 'string' ? record.amountUsd : ''
      if (!txHash && !fromAddress && !amountUsd) {
        return null
      }
      return { txHash, fromAddress, amountUsd }
    })
    .filter((item): item is { txHash: string; fromAddress: string; amountUsd: string } => Boolean(item))
}

const getStatusColorScheme = (status?: string | null): string => {
  switch (status) {
    case 'ready':
      return 'green'
    case 'failed':
      return 'red'
    case 'voided':
      return 'orange'
    case 'queued':
    case 'generating':
      return 'blue'
    default:
      return 'gray'
  }
}

export type OrderDocumentRetentionDisplayRow = Readonly<{
  labelKey: string
  fallbackLabel: string
  value: string | number | null
  timestamp?: boolean
}>

export const buildDocumentRetentionDisplayRows = (
  document: OrderDocumentSummary
): OrderDocumentRetentionDisplayRow[] => [
  {
    labelKey: 'orders.lifecycle.documents.currentAvailability',
    fallbackLabel: 'Current Availability',
    value: document.availability,
  },
  {
    labelKey: 'orders.lifecycle.documents.policyEvaluatedAvailability',
    fallbackLabel: 'Policy-Evaluated Availability',
    value: document.evaluatedAvailability,
  },
  {
    labelKey: 'orders.lifecycle.documents.retentionEvaluationReason',
    fallbackLabel: 'Retention Evaluation Reason',
    value: document.retentionEvaluationReason,
  },
  {
    labelKey: 'orders.lifecycle.documents.retentionState',
    fallbackLabel: 'Retention State',
    value: document.retentionState,
  },
  {
    labelKey: 'orders.lifecycle.documents.retentionBasisAt',
    fallbackLabel: 'Retention Basis At',
    value: document.retentionBasisAt,
    timestamp: true,
  },
  {
    labelKey: 'orders.lifecycle.documents.retainUntil',
    fallbackLabel: 'Retain Until',
    value: document.retainUntil,
    timestamp: true,
  },
  {
    labelKey: 'orders.lifecycle.documents.retentionBlockedReason',
    fallbackLabel: 'Retention Blocked Reason',
    value: document.retentionBlockedReason,
  },
  {
    labelKey: 'orders.lifecycle.documents.purgedAt',
    fallbackLabel: 'Purged At',
    value: document.purgedAt,
    timestamp: true,
  },
  ...(document.retentionState === 'purging'
    ? [{
        labelKey: 'orders.lifecycle.documents.purgeClaimExpiresAt',
        fallbackLabel: 'Purge Claim Expires At',
        value: document.purgeClaimExpiresAt,
        timestamp: true,
      }]
    : []),
  {
    labelKey: 'orders.lifecycle.documents.accessEvaluatedAt',
    fallbackLabel: 'Access Evaluated At',
    value: document.accessEvaluatedAt,
    timestamp: true,
  },
]

export function OrderLifecycleDocumentsTab({
  orderId,
  isActive,
  mode,
  onRefresh,
  refreshToken,
  onLoadingChange,
}: OrderLifecycleDocumentsTabProps) {
  const { t } = useTranslation()
  const locale = typeof window !== 'undefined' ? navigator.language : 'en-US'
  const { success: showSuccessToast, error: showErrorToast } = useToast()
  const [summary, setSummary] = useState<OrderDocumentsResponse | null>(null)
  const [details, setDetails] = useState<Partial<Record<OrderDocumentType, OrderDocumentDetail | null>>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [downloadingType, setDownloadingType] = useState<OrderDocumentType | null>(null)
  const [retryingType, setRetryingType] = useState<OrderDocumentType | null>(null)
  const [hasAttempted, setHasAttempted] = useState(false)
  const requestSequence = useRef(0)
  const lastRefreshToken = useRef(refreshToken)

  useEffect(() => {
    onLoadingChange?.(isLoading)
  }, [isLoading, onLoadingChange])

  useEffect(
    () => () => {
      onLoadingChange?.(false)
    },
    [onLoadingChange]
  )

  useEffect(() => {
    requestSequence.current += 1
    setSummary(null)
    setDetails({})
    setErrorMessage(null)
    setIsLoading(false)
    setDownloadingType(null)
    setRetryingType(null)
    setHasAttempted(false)
  }, [mode, orderId])

  useEffect(
    () => () => {
      requestSequence.current += 1
    },
    []
  )

  const refreshDocuments = useCallback(async () => {
    if (!orderId) {
      return
    }

    const requestId = ++requestSequence.current
    setIsLoading(true)
    setErrorMessage(null)

    try {
      const documentSummary = await orderService.getOrderDocuments(orderId, mode)
      if (requestId !== requestSequence.current) {
        return
      }

      const detailResults = await Promise.allSettled(
        DOCUMENT_TYPES.map(async documentType => ({
          documentType,
          detail: await orderService.getOrderDocument(orderId, documentType, mode),
        }))
      )
      if (requestId !== requestSequence.current) {
        return
      }

      const nextDetails: Partial<Record<OrderDocumentType, OrderDocumentDetail | null>> = {}
      for (const result of detailResults) {
        if (result.status === 'fulfilled') {
          nextDetails[result.value.documentType] = result.value.detail
        }
      }
      setSummary(documentSummary)
      setDetails(nextDetails)

      const rejectedCount = detailResults.filter(result => result.status === 'rejected').length
      if (rejectedCount > 0) {
        setErrorMessage(t(
          'orders.lifecycle.documents.partialError',
          'Some document details could not be loaded.'
        ))
      }
    } catch (error) {
      if (requestId !== requestSequence.current) {
        return
      }
      const message = error instanceof Error
        ? error.message
        : t('orders.lifecycle.documents.loadFailed', 'Failed to load order documents.')
      setErrorMessage(message)
    } finally {
      if (requestId === requestSequence.current) {
        setIsLoading(false)
      }
    }
  }, [mode, orderId, t])

  useEffect(() => {
    if (!isActive || !orderId || hasAttempted) {
      return
    }
    setHasAttempted(true)
    void refreshDocuments()
  }, [hasAttempted, isActive, orderId, refreshDocuments])

  useEffect(() => {
    if (lastRefreshToken.current === refreshToken) {
      return
    }
    if (!isActive || !orderId) {
      lastRefreshToken.current = refreshToken
      return
    }
    if (isLoading) {
      return
    }
    lastRefreshToken.current = refreshToken
    setHasAttempted(true)
    void refreshDocuments()
  }, [isActive, isLoading, orderId, refreshDocuments, refreshToken])

  const handleDownload = useCallback(async (
    documentType: OrderDocumentType,
    fallbackFilename?: string
  ) => {
    if (!orderId) {
      return
    }
    try {
      setDownloadingType(documentType)
      await orderService.downloadOrderDocumentPdf(orderId, documentType, mode, fallbackFilename)
    } catch (error) {
      showErrorToast(
        error instanceof Error
          ? error.message
          : t('orders.lifecycle.documents.downloadFailed', 'Failed to download document.')
      )
    } finally {
      setDownloadingType(null)
    }
  }, [mode, orderId, showErrorToast, t])

  const handleRetry = useCallback(async (documentType: OrderDocumentType) => {
    if (!orderId) {
      return
    }
    try {
      setRetryingType(documentType)
      await orderService.retryOrderDocument(orderId, documentType, mode)
      showSuccessToast(t(
        'orders.lifecycle.documents.retryQueued',
        'Document retry queued successfully.'
      ))
      await refreshDocuments()
      onRefresh?.()
    } catch (error) {
      showErrorToast(
        error instanceof Error
          ? error.message
          : t('orders.lifecycle.documents.retryFailed', 'Failed to retry document generation.')
      )
    } finally {
      setRetryingType(null)
    }
  }, [mode, onRefresh, orderId, refreshDocuments, showErrorToast, showSuccessToast, t])

  const cards = useMemo(() => {
    const documents = summary?.documents
    return DOCUMENT_TYPES.map(documentType => ({
      documentType,
      title: t(
        documentType === 'invoice'
          ? 'orders.lifecycle.documents.invoiceTitle'
          : 'orders.lifecycle.documents.receiptTitle',
        documentType === 'invoice' ? 'Invoice' : 'Receipt'
      ),
      summary: documents?.[documentType] ?? null,
      detail: details[documentType] ?? null,
    }))
  }, [details, summary?.documents, t])

  const renderInfoRow = (label: string, value?: ReactNode | null) => {
    if (value === null || value === undefined || value === '') {
      return null
    }
    return (
      <HStack spacing={3} align="flex-start">
        <Text fontSize="sm" fontWeight="medium" minW="160px">
          {label}
        </Text>
        <Text fontSize="sm" flex="1" wordBreak="break-word">
          {value}
        </Text>
      </HStack>
    )
  }

  const renderRetentionInfoRow = (row: OrderDocumentRetentionDisplayRow) => {
    const formattedValue = row.timestamp && typeof row.value === 'string'
      ? formatTimestamp(row.value, locale)
      : row.value
    return (
      <HStack key={row.labelKey} spacing={3} align="flex-start">
        <Text fontSize="sm" fontWeight="medium" minW="220px">
          {t(row.labelKey, row.fallbackLabel)}
        </Text>
        <Text fontSize="sm" flex="1" wordBreak="break-word">
          {formattedValue === null || formattedValue === '' ? '—' : formattedValue}
        </Text>
      </HStack>
    )
  }

  const renderDocumentCard = (
    documentType: OrderDocumentType,
    title: string,
    documentSummary: OrderDocumentsResponse['documents'][OrderDocumentType] | null,
    documentDetail: OrderDocumentDetail | null
  ) => {
    const current = documentDetail ?? documentSummary
    const snapshot = documentDetail?.snapshot ?? null
    const metadataEntries = getMetadataEntries(snapshot)
    const receiptPayments = documentType === 'receipt' ? getReceiptPayments(snapshot) : []
    const generatedAt = formatTimestamp(current?.generatedAt, locale)
    const fileSize = formatFileSize(documentDetail?.pdf?.fileSizeBytes ?? current?.fileSizeBytes ?? null)
    const documentNumber = current?.documentNumber ?? null
    const fallbackFilename = buildDocumentDownloadFilename(
      documentType,
      documentNumber,
      mode
    )
    const status = current?.status ?? null

    return (
      <Box borderWidth="1px" borderRadius="md" p={4}>
        <VStack align="stretch" spacing={4}>
          <HStack justify="space-between" align="center">
            <Text fontWeight="semibold">{title}</Text>
            <Badge colorScheme={getStatusColorScheme(status)}>
              {status ?? t('orders.lifecycle.documents.statusUnavailable', 'Unavailable')}
            </Badge>
          </HStack>

          <VStack align="stretch" spacing={2}>
            {renderInfoRow(t('orders.lifecycle.documents.documentNumber', 'Document Number'), documentNumber)}
            {renderInfoRow(t('orders.lifecycle.documents.generatedAt', 'Generated At'), generatedAt)}
            {renderInfoRow(
              t('orders.lifecycle.documents.fileSize', 'File Size'),
              fileSize
            )}
            {renderInfoRow(
              t('orders.lifecycle.documents.fileHash', 'File Hash'),
              documentDetail?.pdf?.fileHash ?? current?.fileHash ?? null
            )}
            {renderInfoRow(
              t('orders.lifecycle.documents.failureReason', 'Failure Reason'),
              current?.failureReason ?? null
            )}
            {renderInfoRow(
              t('orders.lifecycle.documents.blockedReason', 'Generation Blocked Reason'),
              current?.blockedReason ?? null
            )}
          </VStack>

          {current && (
            <VStack align="stretch" spacing={2}>
              <Text fontSize="sm" fontWeight="semibold">
                {t('orders.lifecycle.documents.retentionTitle', 'Document Retention')}
              </Text>
              {buildDocumentRetentionDisplayRows(current).map(renderRetentionInfoRow)}
            </VStack>
          )}

          {snapshot && (
            <VStack align="stretch" spacing={2}>
              <Text fontSize="sm" fontWeight="semibold">
                {t('orders.lifecycle.documents.snapshotTitle', 'Document Snapshot')}
              </Text>
              {renderInfoRow(t('orders.table.orderId'), getSnapshotString(snapshot, 'orderId'))}
              {renderInfoRow(t('orders.lifecycle.documents.issuedAt', 'Issued At'), formatTimestamp(getSnapshotString(snapshot, 'issuedAt'), locale))}
              {renderInfoRow(t('orders.lifecycle.documents.network', 'Network'), getSnapshotString(snapshot, 'networkEnvironment'))}
              {documentType === 'invoice' && (
                <>
                  {renderInfoRow(t('orders.lifecycle.amountUsd'), getSnapshotString(snapshot, 'amountUsd'))}
                  {renderInfoRow(t('orders.lifecycle.expiresAt'), formatTimestamp(getSnapshotString(snapshot, 'expiresAt'), locale))}
                  {renderInfoRow(
                    t('orders.lifecycle.documents.checkoutSessionUrl', 'Checkout Session URL'),
                    (() => {
                      const checkoutSessionUrl = getSnapshotString(snapshot, 'checkoutSessionUrl')
                      if (!checkoutSessionUrl) {
                        return null
                      }
                      return (
                        <Link
                          href={checkoutSessionUrl}
                          color="blue.500"
                          textDecoration="underline"
                          isExternal
                        >
                          {t(
                            'orders.lifecycle.documents.checkoutSessionLink',
                            'Open checkout session'
                          )}
                        </Link>
                      )
                    })()
                  )}
                  {renderInfoRow(
                    t('orders.lifecycle.documents.statusAtGeneration', 'Status At Generation'),
                    getSnapshotString(snapshot, 'statusAtGeneration')
                  )}
                </>
              )}
              {documentType === 'receipt' && (
                <>
                  {renderInfoRow(
                    t('orders.lifecycle.documents.orderStatus', 'Order Status'),
                    getSnapshotString(snapshot, 'orderStatus')
                  )}
                  {renderInfoRow(
                    t('orders.lifecycle.documents.expectedAmountUsd', 'Expected Amount (USD)'),
                    getSnapshotString(snapshot, 'expectedAmountUsd')
                  )}
                  {renderInfoRow(
                    t('orders.lifecycle.documents.confirmedTotalAmountUsd', 'Confirmed Total (USD)'),
                    getSnapshotString(snapshot, 'confirmedTotalAmountUsd')
                  )}
                  {renderInfoRow(
                    t('orders.lifecycle.documents.paymentWindowClosedAt', 'Payment Window Closed At'),
                    formatTimestamp(getSnapshotString(snapshot, 'paymentWindowClosedAt'), locale)
                  )}
                </>
              )}

              {metadataEntries.length > 0 && (
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    {t('orders.lifecycle.documents.metadata', 'Metadata')}
                  </Text>
                  <List spacing={2}>
                    {metadataEntries.map(entry => (
                      <ListItem key={entry.key}>
                        <Text fontSize="sm">
                          <Text as="span" fontWeight="medium">{entry.key}:</Text>{' '}
                          {entry.displayValue}
                          {Number.isFinite(entry.charLength) ? ` (${entry.charLength})` : ''}
                        </Text>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {receiptPayments.length > 0 && (
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    {t('orders.lifecycle.documents.payments', 'Payments')}
                  </Text>
                  <List spacing={2}>
                    {receiptPayments.map(payment => (
                      <ListItem key={`${payment.txHash}-${payment.fromAddress}`}>
                        <VStack align="stretch" spacing={1}>
                          <Text fontSize="sm">
                            <Text as="span" fontWeight="medium">
                              {t('orders.lifecycle.txHash')}:
                            </Text>{' '}
                            <Code fontSize="xs">{truncateMiddle(payment.txHash)}</Code>
                          </Text>
                          <Text fontSize="sm">
                            <Text as="span" fontWeight="medium">
                              {t('orders.lifecycle.from')}:
                            </Text>{' '}
                            <Code fontSize="xs">{truncateMiddle(payment.fromAddress)}</Code>
                          </Text>
                          <Text fontSize="sm">
                            <Text as="span" fontWeight="medium">
                              {t('orders.lifecycle.amountUsd')}:
                            </Text>{' '}
                            {payment.amountUsd}
                          </Text>
                        </VStack>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </VStack>
          )}

          <HStack spacing={3}>
            <Button
              size="sm"
              onClick={() => void handleDownload(documentType, fallbackFilename)}
              isLoading={downloadingType === documentType}
              loadingText={t('orders.lifecycle.documents.download', 'Download')}
              isDisabled={
                !current?.canDownloadPdf ||
                (downloadingType !== null && downloadingType !== documentType)
              }
            >
              {t('orders.lifecycle.documents.download', 'Download')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void handleRetry(documentType)}
              isLoading={retryingType === documentType}
              isDisabled={
                current?.status === 'queued' ||
                current?.status === 'generating' ||
                (retryingType !== null && retryingType !== documentType)
              }
            >
              {t('orders.lifecycle.documents.retry', 'Retry Generation')}
            </Button>
          </HStack>
        </VStack>
      </Box>
    )
  }

  if (!orderId) {
    return (
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        <Text fontSize="sm">
          {t('orders.lifecycle.documents.noOrder', 'Order ID is missing.')}
        </Text>
      </Alert>
    )
  }

  return (
    <VStack align="stretch" spacing={4}>
      {errorMessage && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">{errorMessage}</Text>
        </Alert>
      )}

      {summary && (
        <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
          {cards.map(card => (
            <Box key={card.documentType}>
              {renderDocumentCard(card.documentType, card.title, card.summary, card.detail)}
            </Box>
          ))}
        </SimpleGrid>
      )}
    </VStack>
  )
}
